import { useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure, useTrackOnce } from '#src/app/shared/hooks/tracking';

import { useSignerVelocityExperiment } from '../useSignerVelocityExperiment';

type SignerVelocityExperiment = PetitionDetailsPageUserData['experiments']['petitionPageSignerVelocityExperiment'];

/**
 * Passed to useTrackExperimentExposure (which must be called unconditionally, per Rules of Hooks) while
 * the visitor is unqualified — `treated: false` keeps it a no-op.
 */
const INERT_EXPERIMENT = {
	type: 'experiment-service',
	experimentName: 'petition_page_signer_velocity_experiment',
	variation: 'control',
	config: {},
	treated: false,
} as SignerVelocityExperiment;

/**
 * Treats `petition_page_signer_velocity_experiment` and fires `$exposure` the moment the visitor
 * qualifies — unsigned, on a signable petition above the daily ignition floor. Runs on desktop and mobile
 * alike.
 *
 * Fires for every arm, control and fake_control included — they are analysis arms, and `qualified` is
 * computed identically for all three. Deliberately NOT viewport-gated: control renders no pill, so there
 * is nothing to observe, and gating one arm on visibility would make the arms incomparable.
 *
 * Mount this exactly once, from a location that renders in every arm. Mounting it inside a pill would mean
 * control never fires.
 */
export function useTreatSignerVelocityExperiment(): void {
	const utilityContext = useUtilityContext();
	const { experiment, qualified, isRealAssignment, petitionId } = useSignerVelocityExperiment();
	const [treatedLocally, setTreatedLocally] = useState(false);
	// Ref, not state: a second synchronous pass in the same tick would still read state's pre-update value.
	// Holds the petition rather than a boolean — SPA nav reuses this component without unmounting, so a
	// boolean would leave the new petition permanently untreated.
	const treatedPetitionRef = useRef<string | null>(null);

	const resolvedExperiment = useMemo<SignerVelocityExperiment>(() => {
		if (!qualified) return INERT_EXPERIMENT;
		// Not just our own treat resolving. `experiment.treated` covers a session that arrived treated —
		// unreachable while the normalizer forces it false, but the exposure path shouldn't depend on that.
		// `override` is needed because useTrackExperimentExposure short-circuits on `!treated` before
		// reaching its `override` branch, so QA would never get the "$exposure SUPPRESSED by override" log.
		// No real exposure leaks: that branch logs and returns before trackExposureOnce.
		const treated = treatedLocally || experiment.treated || Boolean(experiment.override);
		return treated ? { ...experiment, treated: true } : experiment;
	}, [qualified, experiment, treatedLocally]);

	// `resetKeys` is required, not optional: variations are deterministic per user, so without the petition
	// id an SPA petition-to-petition navigation would dedup the new petition's legitimate exposure.
	const trackExperimentExposure = useTrackExperimentExposure(resolvedExperiment, { resetKeys: [petitionId] });

	useEffect(() => {
		if (!qualified || !isRealAssignment || treatedPetitionRef.current === petitionId) return;
		treatedPetitionRef.current = petitionId;
		(async () => {
			// treatExperiment swallows its own errors and always resolves, so a silent server-side failure is
			// indistinguishable from success — the same tradeoff every treat-on-X caller accepts.
			await treatExperiment(utilityContext, experiment.experimentName);
			setTreatedLocally(true);
		})().catch((e: unknown) => {
			// Backstop if treatExperiment's resolve-never-reject contract ever regresses — this must not be
			// silent, since it would drop $exposure for the whole experiment.
			void utilityContext.errorReporter.report({
				error:
					e instanceof Error
						? e
						: new Error('useTreatSignerVelocityExperiment: unexpected rejection from treatExperiment'),
				params: { experimentName: experiment.experimentName },
			});
		});
	}, [qualified, isRealAssignment, experiment, utilityContext, petitionId]);

	// Keyed off the resolved `treated`, not `treatedLocally`, so an override and an already-treated session
	// both reach the exposure call. INERT_EXPERIMENT is `treated: false`, so unqualified needs no check.
	useEffect(() => {
		if (!resolvedExperiment.treated) return;
		void trackExperimentExposure();
	}, [resolvedExperiment.treated, trackExperimentExposure]);
}

/**
 * Fires `petition_page_signer_velocity_suppressed` once per pageview when the visitor is in scope but no
 * pill can render — the diagnostic for how much in-scope traffic the floor and signability remove.
 *
 * Every arm, but real assignments only: fallback and QA-override sessions are never exposed, so their
 * suppressions would report volume for traffic that was never in the experiment.
 */
export function useTrackSignerVelocitySuppressed(): void {
	const { suppressedReason, dailySignatureCount, floor, isRealAssignment, petitionId } = useSignerVelocityExperiment();
	const trackOnce = useTrackOnce([suppressedReason, petitionId]);

	useEffect(() => {
		if (!suppressedReason || !isRealAssignment) return;
		void trackOnce('petition_page_signer_velocity_suppressed', {
			petition_id: petitionId,
			signer_velocity_suppressed_reason: suppressedReason,
			daily_signature_count: dailySignatureCount,
			signer_velocity_floor: floor,
		});
	}, [suppressedReason, isRealAssignment, petitionId, dailySignatureCount, floor, trackOnce]);
}
